import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { loadKakaoMaps } from '../map';
import tokens from '../theme/tokens';

// 배송 지도 — 출발·도착 표식, 출발지 → 도착지 도로 경로(밑그림, 옅은 선), 기사 위치(코랄 점, 숨 쉬듯 퍼지는 링), 기사 → 목적지 도로 경로(주황 선).
// 자료가 새로 오면 지도를 다시 만들지 않고 표식·선만 옮긴다. 기사가 화면 밖으로 나가면 범위를 다시 맞춘다.
// SDK 를 못 불러오면(키·도메인 미등록) onError 를 부르고 아무것도 그리지 않는다 — 호출 쪽이 지도 칸을 숨긴다
const useStyles = makeStyles(() => ({
  root: {
    position: 'relative',
    width: '100%',
    height: 240,
    borderRadius: tokens.radiusLg,
    overflow: 'hidden',
    border: `1px solid ${tokens.line}`,
    backgroundColor: tokens.surfaceSoft,
  },
  canvas: { width: '100%', height: '100%' },
}));

const RIDER_CSS = `
.qtk-rider{position:relative;width:16px;height:16px;transform:translate(0,0)}
.qtk-rider i{position:absolute;left:0;top:0;width:16px;height:16px;border-radius:50%;background:#ff7b6b;border:3px solid #fff;box-sizing:border-box;box-shadow:0 1px 4px rgba(0,0,0,.25)}
.qtk-rider:before{content:"";position:absolute;left:-10px;top:-10px;width:36px;height:36px;border-radius:50%;background:rgba(255,123,107,.35);animation:qtk-pulse 1.8s ease-out infinite}
@keyframes qtk-pulse{0%{transform:scale(.5);opacity:.9}100%{transform:scale(1.4);opacity:0}}
@media (prefers-reduced-motion: reduce){.qtk-rider:before{animation:none;opacity:.35}}
`;
let cssInjected = false;
function injectCss() {
  if (cssInjected || typeof document === 'undefined') return;
  const el = document.createElement('style');
  el.textContent = RIDER_CSS;
  document.head.appendChild(el);
  cssInjected = true;
}

const pin = (label, fill) => `<div style="display:inline-flex;align-items:center;height:24px;padding:0 9px;border-radius:12px;background:${fill};color:#fff;font:700 12px/1 'Noto Sans KR',sans-serif;letter-spacing:-0.3px;box-shadow:0 2px 6px rgba(0,0,0,.18);transform:translateY(-12px);white-space:nowrap">${label}</div>`;
const riderDot = () => '<div class="qtk-rider"><i></i></div>';

function TrackMap({ map, onError }) {
  const cs = useStyles();
  const ref = useRef(null);
  const st = useRef({ maps: null, map: null, rider: null, line: null, route: null, pins: [], fitted: false });
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!ref.current) return undefined;
    injectCss();
    loadKakaoMaps().then((maps) => {
      if (!alive || !ref.current) return;
      const c = map.rider || map.start || map.dest || { lat: 37.5665, lon: 126.978 };
      st.current.maps = maps;
      st.current.map = new maps.Map(ref.current, { center: new maps.LatLng(c.lat, c.lon), level: 6 });
      setReady(true);
    }).catch(() => { if (!alive) return; setFailed(true); if (onError) onError(); });
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const s = st.current;
    if (!ready || !s.map || !map) return;
    const { maps } = s;
    const pts = [];

    s.pins.forEach((p) => p.setMap(null));
    s.pins = [];
    if (map.start) {
      const p = new maps.LatLng(map.start.lat, map.start.lon);
      s.pins.push(new maps.CustomOverlay({ map: s.map, position: p, content: pin('출발', tokens.ink), yAnchor: 1 }));
      pts.push(p);
    }
    if (map.dest) {
      const p = new maps.LatLng(map.dest.lat, map.dest.lon);
      s.pins.push(new maps.CustomOverlay({ map: s.map, position: p, content: pin('도착', tokens.primaryDark), yAnchor: 1 }));
      pts.push(p);
    }

    let riderPt = null;
    if (map.rider) {
      riderPt = new maps.LatLng(map.rider.lat, map.rider.lon);
      if (!s.rider) s.rider = new maps.CustomOverlay({ map: s.map, position: riderPt, content: riderDot(), xAnchor: 0.5, yAnchor: 0.5, zIndex: 5 });
      else s.rider.setPosition(riderPt);
      pts.push(riderPt);
    } else if (s.rider) {
      s.rider.setMap(null);
      s.rider = null;
    }

    // 출발지 → 도착지 전체 경로(밑그림). 기사 경로가 따로 있으면 옅게, 없으면(배차 전·완료) 이 선이 주황 본선
    const hasRiderPath = Array.isArray(map.path) && map.path.length > 1;
    if (s.route) { s.route.setMap(null); s.route = null; }
    if (Array.isArray(map.route) && map.route.length > 1) {
      const routePath = map.route.map(([lat, lon]) => new maps.LatLng(lat, lon));
      s.route = new maps.Polyline({
        map: s.map, path: routePath, strokeWeight: hasRiderPath ? 4 : 5,
        strokeColor: hasRiderPath ? '#ffc0b4' : tokens.primary, strokeOpacity: hasRiderPath ? 0.9 : 0.95,
        strokeStyle: map.route.length > 2 ? 'solid' : 'shortdash', zIndex: 1,
      });
      routePath.forEach((p) => pts.push(p));
    }

    if (s.line) { s.line.setMap(null); s.line = null; }
    if (hasRiderPath) {
      const path = map.path.map(([lat, lon]) => new maps.LatLng(lat, lon));
      s.line = new maps.Polyline({ map: s.map, path, strokeWeight: 5, strokeColor: tokens.primary, strokeOpacity: 0.95, strokeStyle: 'solid', zIndex: 2 });
      path.forEach((p) => pts.push(p));
    }

    if (!pts.length) return;
    const needFit = !s.fitted || (riderPt && !s.map.getBounds().contain(riderPt));
    if (needFit) {
      const b = new maps.LatLngBounds();
      pts.forEach((p) => b.extend(p));
      s.map.setBounds(b, 36, 36, 36, 36);
      s.fitted = true;
    }
  }, [ready, map]);

  if (failed) return null;
  return (
    <div className={cs.root} aria-label="배송 지도">
      <div ref={ref} className={cs.canvas} />
    </div>
  );
}

export default TrackMap;
